import { useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import type { EmailQuestionErrorCode } from "@/hooks/voice/useChannelRequests";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_QUESTION_LENGTH = 2000;

const ERROR_COPY: Record<EmailQuestionErrorCode, string> = {
  no_mailbox: "This Genie can't receive email yet — try one of the other ways to connect.",
  rate_limited: "Too many questions sent right now. Please try again in a few minutes.",
  unknown: "Something went wrong sending that. Please try again.",
};

export interface EmailStageSubmitResult {
  ok: boolean;
  errorCode?: EmailQuestionErrorCode;
  errorMessage?: string;
}

interface EmailStageProps {
  brandColor: string;
  secondaryColor?: string | null;
  agentName?: string | null;
  onSubmit: (args: {
    visitorEmail: string;
    visitorName?: string;
    question: string;
    pageUrl: string;
  }) => Promise<EmailStageSubmitResult>;
  pageUrl: string;
}

/**
 * The "Email" channel stage — fills the switcher's stage with a one-shot
 * question form. The caller only shows this tab when the Genie has a
 * mailbox; a 409 here means that changed mid-session, so it's handled the
 * same as any other error rather than assumed impossible.
 */
export const EmailStage = ({ brandColor, secondaryColor, agentName, onSubmit, pageUrl }: EmailStageProps) => {
  const end = secondaryColor || "#FF4DB8";
  const gradient = `linear-gradient(90deg, ${brandColor} 0%, ${end} 100%)`;

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [question, setQuestion] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<EmailStageSubmitResult | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedQuestion = question.trim();
    let hasError = false;

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setEmailError("Enter a valid email address.");
      hasError = true;
    } else {
      setEmailError(null);
    }

    if (!trimmedQuestion) {
      setQuestionError("Tell the Genie what you need.");
      hasError = true;
    } else if (trimmedQuestion.length > MAX_QUESTION_LENGTH) {
      setQuestionError(`Keep it under ${MAX_QUESTION_LENGTH} characters.`);
      hasError = true;
    } else {
      setQuestionError(null);
    }

    if (hasError) return;

    setSubmitting(true);
    setResult(null);
    try {
      const res = await onSubmit({
        visitorEmail: trimmedEmail,
        visitorName: name.trim() || undefined,
        question: trimmedQuestion,
        pageUrl,
      });
      setResult(res);
    } finally {
      setSubmitting(false);
    }
  };

  if (result?.ok) {
    return (
      <div className="flex w-full max-w-[420px] items-center gap-3 rounded-3xl border border-[#E9EDF3] bg-white p-5 shadow-[0_10px_30px_-24px_rgba(15,23,42,0.4)]">
        <div
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-white"
          style={{ background: gradient, boxShadow: `0 10px 24px -10px ${brandColor}80` }}
        >
          <Check size={22} strokeWidth={2.75} />
        </div>
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold leading-tight text-[#222B45]">Sent. Watch your inbox.</h3>
          <p className="mt-0.5 text-[13px] leading-snug text-[#64748B]">
            {agentName || "The Genie"} will email you back at <CopyableEmail email={email.trim()} />.
          </p>
        </div>
      </div>
    );
  }

  const inputClass =
    "w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-genie-primary transition-shadow";

  return (
    <div className="w-full max-w-[420px] rounded-3xl border border-[#E9EDF3] bg-white p-5 shadow-[0_10px_30px_-24px_rgba(15,23,42,0.4)]">
      <div className="mb-4 flex items-center gap-3">
        <span
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl"
          style={{ background: `linear-gradient(135deg, ${brandColor}1a, ${end}1a)` }}
        >
          <GenieIcon name="mail" size={20} color={brandColor} secondaryColor={end} gradientEnabled />
        </span>
        <div className="min-w-0">
          <h3 className="text-[16px] font-semibold leading-tight text-[#222B45]">Email {agentName || "the Genie"}</h3>
          <p className="mt-0.5 text-[13px] leading-snug text-[#64748B]">The Genie will email you back at this address.</p>
        </div>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-3">
        <div>
          <label htmlFor="email-stage-email" className="sr-only">
            Your email
          </label>
          <input
            id="email-stage-email"
            type="email"
            required
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            placeholder="you@example.com"
            className={inputClass}
          />
          {emailError && <p className="mt-1.5 text-[13px] font-medium text-red-600">{emailError}</p>}
        </div>

        <div>
          <label htmlFor="email-stage-name" className="sr-only">
            Your name
          </label>
          <input
            id="email-stage-name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="email-stage-question" className="sr-only">
            Your question
          </label>
          <textarea
            id="email-stage-question"
            required
            rows={4}
            maxLength={MAX_QUESTION_LENGTH}
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
              if (questionError) setQuestionError(null);
            }}
            placeholder="What do you need help with?"
            className={`${inputClass} resize-none`}
          />
          {questionError && <p className="mt-1.5 text-[13px] font-medium text-red-600">{questionError}</p>}
        </div>

        {result && !result.ok && (
          <p className="flex items-start gap-1.5 text-[13px] font-medium text-red-600">
            <AlertTriangle size={14} strokeWidth={2.25} className="mt-0.5 flex-shrink-0" />
            {ERROR_COPY[result.errorCode || "unknown"]}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3 text-[15px] font-semibold text-white shadow-lg transition-all hover:scale-[1.02] hover:opacity-95 disabled:cursor-wait disabled:opacity-70 disabled:hover:scale-100"
          style={{ background: gradient, boxShadow: `0 10px 28px -10px ${brandColor}80` }}
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Sending…
            </>
          ) : (
            "Send to the Genie"
          )}
        </button>
      </form>
    </div>
  );
};

export default EmailStage;
